import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import ReportProblemButton from '../../support/components/ReportProblemButton';
import {
  PAYMENT_STATUS_CLASSES,
  PAYMENT_STATUS_LABELS,
} from '../../../shared/constants/paymentStatuses';
import MemberPaymentSubmitModal from './MemberPaymentSubmitModal';
import CollectorPayBlock from './CollectorPayBlock';
import ProofThumbnails from '../../../shared/components/ProofThumbnails';
import CollectorPaymentsToMe from './CollectorPaymentsToMe';
import CollectorCollectionProgress from './CollectorCollectionProgress';
import FeePaymentSubmitModal from './FeePaymentSubmitModal';
import BottomNav from './BottomNav';
import {
  MEMBER_STATE,
  resolveMemberRoundState,
  getRoundPayment,
  getLiveRound,
  hasCollectedThisCycle,
  collectedTimestamp,
  memberCollectedAmount,
  isCycleComplete,
  getScheduledCollectionDate,
  getRoundPaymentDueDate,
  getCollectionPosition,
  formatScheduleDate,
  confirmReceipt,
} from '../../cycle-engine';
import { checkFeatureFlag, FEATURE_FLAGS } from '../../../shared/utils/featureFlags';
import { memberConstraintMessage } from '../../../shared/utils/memberConstraintError';
import { fetchMemberFees, outstandingTotal } from '../../../shared/utils/lateFees';
import { downloadAgreementPdf } from '../utils/downloadAgreementPdf';
import { fetchMemberRecord } from '../../../shared/utils/memberRecord';
import { downloadMemberRecordPdf } from '../../../shared/utils/downloadMemberRecordPdf';
import DeleteAccountModal from './DeleteAccountModal';
import ReconsentModal from './ReconsentModal';
import { MESSAGING_CONSENT_VERSION } from '../../onboarding/constants/policyVersions';
import { useRefetchOnActive } from '../../../shared/hooks/useRefetchOnActive';

const MEMBER_TABS = ['home', 'payments', 'schedule', 'account'];

const MemberDashboard = () => {
  const navigate = useNavigate();
  // Persist the active tab in the URL (?tab=<name>) so a refresh restores it and
  // the view is linkable. Falls back to 'home' for a missing/unknown value.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    MEMBER_TABS.some(t => t.key === tabFromUrl) ? tabFromUrl : 'home',
  );
  const changeTab = (tab) => {
    setActiveTab(tab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [hands, setHands] = useState([]);
  const [memberFees, setMemberFees] = useState([]);  // own late fees (separate from contributions/pot)
  const [feeModal, setFeeModal] = useState(null);    // the fee being submitted (member handshake)
  const [myConsent, setMyConsent] = useState(null);  // own latest agreement acknowledgment
  const [payments, setPayments] = useState([]);
  const [groupData, setGroupData] = useState(null);
  // Own certified record (H6). Loaded via get_member_record (own-record-only).
  const [myRecord, setMyRecord] = useState(null);
  const [exportingRecord, setExportingRecord] = useState(false);
  const [recordExportError, setRecordExportError] = useState(null);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [collectorRefresh, setCollectorRefresh] = useState(0);   // bump to refetch the progress card after a confirm
  const [needsReconsent, setNeedsReconsent] = useState(false);   // member lacks current messaging consent version
  const [reconsentDismissed, setReconsentDismissed] = useState(false);  // "Maybe later" — this session only
  const [allGroupHands, setAllGroupHands] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [myPayouts, setMyPayouts] = useState([]);       // own payouts (full, incl. amount)
  const [rounds, setRounds] = useState([]);
  const [groupPayouts, setGroupPayouts] = useState([]); // group collection-state (safe view; no amounts)
  const [susuOn, setSusuOn] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submittingHand, setSubmittingHand] = useState(null);
  // Current round's collector pay details (direct groups only), from
  // get_round_collector_payinfo (Stage 29). Null when not a direct group / no live
  // round / not applicable — both surfaces render nothing then.
  const [collectorPayinfo, setCollectorPayinfo] = useState(null);
  // Who-paid-whom per round (direct groups), from get_group_round_contributions
  // (Stage 33). Rows: round_number, collector_name, contributor_name, amount,
  // paid_date, status. Empty in Model B. Display-only.
  const [groupContributions, setGroupContributions] = useState([]);
  const [prefs, setPrefs] = useState({
    whatsapp_number: '',
    sms_number: '',
    email: '',
    notification_preference: 'email',
    // Display alias (Stage 46): other MEMBERS see this instead of the real name.
    // Admin + official/FHA records always use the real legal name. Empty = real name.
    display_alias: '',
    // Payment handles (direct groups): where members send this member their pot
    // when it's their collecting turn. Optional; self-editable (own-row).
    zelle_identifier: '',
    cashapp_handle: '',
    venmo_handle: '',
  });

  // In-flight guard: focus + visibility can fire together, and a tab switch may
  // overlap a mount fetch — collapse any overlap to a single request.
  const fetchingRef = useRef(false);
  const fetchData = async ({ silent = false } = {}) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      if (!silent) setLoading(true);  // silent = background refetch, no spinner

      const { data: { user }, error: authError } = await supabaseClient.auth.getUser();
      if (authError || !user) { navigate('/'); return; }

      const { data: member } = await supabaseClient
        .from('members').select('*').eq('auth_user_id', user.id).single();

      if (!member) { setLoading(false); return; }

      setMemberData(member);
      setPrefs({
        whatsapp_number: member.whatsapp_number || '',
        sms_number: member.sms_number || '',
        email: member.email || '',
        notification_preference: member.notification_preference || 'email',
        display_alias: member.display_alias || '',
        zelle_identifier: member.zelle_identifier || '',
        cashapp_handle: member.cashapp_handle || '',
        venmo_handle: member.venmo_handle || '',
      });

      const { data: handsData } = await supabaseClient
        .from('hands').select('*').eq('member_db_id', member.id);
      setHands(handsData || []);

      // Own late fees (RLS scopes to this member). Separate from contributions.
      setMemberFees(await fetchMemberFees([member.id]));

      // Own latest agreement acknowledgment (RLS: member reads own consents).
      const { data: consentRows } = await supabaseClient
        .from('member_consents')
        .select('agreed_at, terms_version, signature, terms_sha256, privacy_accepted, privacy_version, tc_accepted, tc_version')
        .eq('member_db_id', member.id)
        .order('agreed_at', { ascending: false })
        .limit(1);
      setMyConsent(consentRows?.[0] || null);

      // Messaging re-consent gate (piece 3): has this member accepted the CURRENT
      // messaging consent version? Existence check (order-independent).
      const { data: msgRows } = await supabaseClient
        .from('member_consents')
        .select('id')
        .eq('member_db_id', member.id)
        .eq('messaging_consent_version', MESSAGING_CONSENT_VERSION)
        .limit(1);
      setNeedsReconsent(!(msgRows && msgRows.length));

      const handIds = (handsData || []).map(h => h.id);

      if (handIds.length > 0) {
        const { data: paymentsData } = await supabaseClient
          .from('payments').select('*').in('hand_id', handIds);
        setPayments(paymentsData || []);
        console.log('payments loaded:', paymentsData);

        // The member's OWN payouts (own-row RLS, full columns incl. amount) —
        // powers the payout banner AND the member's own "Total Collected" $.
        // Group-wide collection-state (for the rotation resolver) comes from the
        // is_active-safe group_payout_status view below, which has no amounts.
        const { data: payoutsData } = await supabaseClient
          .from('payouts').select('*')
          .in('hand_id', handIds);
        setMyPayouts(payoutsData || []);
        setPendingPayouts((payoutsData || []).filter(p => p.status === 'pending_confirmation'));
      }

      if (member.group_id) {
        const { data: group } = await supabaseClient
          .from('groups').select('*').eq('id', member.group_id).single();
        setGroupData(group);

        // Direct groups only: the current round's collector pay details (scoped,
        // just-in-time via the Stage 29 RPC). Skipped in Model B (also returns
        // nothing there). One call feeds both the submit modal + the payment-due
        // alert. Display-only.
        if (group?.money_flow_model === 'direct_to_collector') {
          const { data: payinfo } = await supabaseClient
            .rpc('get_round_collector_payinfo', { p_group_id: member.group_id });
          setCollectorPayinfo(payinfo?.[0] || null);
          // Who-paid-whom per round (Stage 33) — feeds the rotation contributor list.
          const { data: contribs } = await supabaseClient
            .rpc('get_group_round_contributions', { p_group_id: member.group_id });
          setGroupContributions(contribs || []);
        } else {
          setCollectorPayinfo(null);
          setGroupContributions([]);
        }

        const { data: allHands } = await supabaseClient
          .from('hands').select('*').eq('group_id', member.group_id)
          .order('position_number', { ascending: true });
        setAllGroupHands(allHands || []);

        // Peer roster NAMES only, via the group_member_names safe view (Stage 13).
        // The members table stays own-row RLS — phone/email are never read here.
        const { data: members } = await supabaseClient
          .from('group_member_names').select('id, name').eq('group_id', member.group_id);
        setGroupMembers(members || []);

        // susu_rounds: the group's rounds (no PII) + group-wide COLLECTION-STATE
        // via the group_payout_status safe view (Stage 13) — the resolver needs
        // every member's collected/turn state, but NOT their payout amounts. The
        // payouts table stays own-row RLS; amounts come from myPayouts above.
        const { data: roundsData } = await supabaseClient
          .from('rounds').select('*').eq('group_id', member.group_id);
        setRounds(roundsData || []);
        const { data: groupPayoutsData } = await supabaseClient
          .from('group_payout_status').select('*').eq('group_id', member.group_id);
        setGroupPayouts(groupPayoutsData || []);
      }

      setSusuOn(await checkFeatureFlag(FEATURE_FLAGS.SUSU_ROUNDS));

      // Fetch unread in-app notifications (silently ignore if table not yet created)
      try {
        const { data: notifData } = await supabaseClient
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .eq('read', false)
          .order('created_at', { ascending: false });
        setNotifications(notifData || []);
      } catch {
        setNotifications([]);
      }

    } catch (err) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard');
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Refetch on tab switch (skip the redundant first-render run — mount already
  // fetched). Silent so data swaps in without the full-screen spinner.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) fetchData({ silent: true });
    else didMountRef.current = true;
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when the tab/window regains focus or becomes visible again.
  useRefetchOnActive(() => fetchData({ silent: true }));

  const handleDismissNotification = async (notifId) => {
    try {
      await supabaseClient.from('notifications').update({ read: true }).eq('id', notifId);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch {}
  };

  // Confirm receipt is the PRIMARY advance trigger — route through the cycle
  // engine (confirm_receipt_and_advance RPC), not a raw payouts update.
  const handleConfirmPayout = async (payoutId) => {
    try {
      await confirmReceipt(payoutId);
      await fetchData();
    } catch (err) {
      console.error('Confirm error:', err);
      alert(err.message || 'Failed to confirm payout. Please try again.');
    }
  };

  const handleDisputePayout = async (payoutId) => {
    try {
      await supabaseClient.from('payouts').update({
        status: 'disputed',
        disputed_at: new Date().toISOString(),
      }).eq('id', payoutId);
      await fetchData();
    } catch (err) {
      console.error('Dispute error:', err);
      alert('Failed to dispute payout. Please try again.');
    }
  };

  const handleUpdatePrefs = async () => {
    try {
      // supabase-js does NOT throw on a DB error — it resolves with { error }.
      // We MUST inspect `error`; assuming success here was the false-success bug
      // (a duplicate number was rejected by a unique index yet reported saved,
      // then reverted on refresh). Only claim success when there was no error.
      const { error } = await supabaseClient.from('members').update({
        whatsapp_number: prefs.whatsapp_number,
        sms_number: prefs.sms_number,
        email: prefs.email,
        notification_preference: prefs.notification_preference,
        display_alias: prefs.display_alias?.trim().slice(0, 40) || null,   // empty -> real name
        zelle_identifier: prefs.zelle_identifier?.trim() || null,
        cashapp_handle: prefs.cashapp_handle?.trim() || null,
        venmo_handle: prefs.venmo_handle?.trim() || null,
      }).eq('id', memberData.id);
      if (error) {
        alert(memberConstraintMessage(error) || 'Failed to update preferences.');
        return;
      }
      alert('Preferences updated successfully');
      // Refresh so views reading memberData (e.g. the collector "add payment
      // details" banner) reflect the saved handles immediately.
      fetchData();
    } catch (err) {
      alert('Failed to update preferences.');
    }
  };

  // Load the member's own two-sided record (own-record-only via get_member_record)
  // so the certified-export button can gate on the organizer attestation.
  useEffect(() => {
    if (!memberData?.id || !memberData?.group_id) return;
    let on = true;
    fetchMemberRecord({ memberId: memberData.id, groupId: memberData.group_id })
      .then(r => { if (on) setMyRecord(r); })
      .catch(() => { if (on) setMyRecord(null); });
    return () => { on = false; };
  }, [memberData?.id, memberData?.group_id]);

  const handleExportMyRecord = async () => {
    setExportingRecord(true); setRecordExportError(null);
    try {
      await downloadMemberRecordPdf(myRecord, { groupName: groupData?.group_name });
    } catch (err) {
      setRecordExportError(err.message || 'Could not generate your record PDF.');
    } finally {
      setExportingRecord(false);
    }
  };

  // Live round + round-scoped payment (replaces the hand_id-only lookup).
  const currentRound = getLiveRound(rounds, memberData?.group_id);
  // The cycle to resolve collections/dates against: the live cycle, or (once the
  // cycle completes and currentRound is null) the latest cycle.
  const effectiveCycle = currentRound?.cycle_number
    ?? (rounds.length ? Math.max(...rounds.map(r => Number(r.cycle_number))) : null);
  const getHandPayment = (hand) => getRoundPayment(hand, currentRound, payments);

  const getHandStatus = (hand) => {
    const payment = getHandPayment(hand);
    return payment?.status || 'pending';
  };

  // Payment-due date READS the live round's stored payment_due_date (Stage 9).
  // Flag-gated: susu_rounds OFF is a complete kill switch — no leftover date text.
  const getPaymentDueDate = () =>
    (susuOn ? formatScheduleDate(getRoundPaymentDueDate(currentRound)) : null);

  const getDaysUntilPaymentDue = () => {
    if (!susuOn) return null;
    const raw = getRoundPaymentDueDate(currentRound);
    if (!raw) return null;
    const s = typeof raw === 'string' ? raw.slice(0, 10) : new Date(raw).toISOString().slice(0, 10);
    const [y, m, d] = s.split('-').map(Number);
    return Math.ceil((new Date(y, m - 1, d) - new Date()) / (1000 * 60 * 60 * 24));
  };

  // Collection date READS the stored scheduled_collection_date of the hand's
  // collector-round in the effective cycle (Stage 9). Flag-gated (kill switch).
  const getPayoutCollectionDate = (hand) =>
    (susuOn ? formatScheduleDate(getScheduledCollectionDate(hand, rounds, effectiveCycle)) : null);

  // Turn/collection state now comes from the shared resolver — authoritative
  // rounds order, no dates, no collected_amount. The admin dashboard uses the
  // SAME resolver, so member and admin views can't drift.
  const memberRoundState = (hand) => resolveMemberRoundState(hand, rounds, payments, groupPayouts);
  const isTurnToCollect = (hand) => memberRoundState(hand) === MEMBER_STATE.UP_NOW;
  const handCollectedThisCycle = (hand) =>
    hasCollectedThisCycle(hand, effectiveCycle, groupPayouts, rounds);
  // Terminal state: the cycle is over (no live round + all confirmed). Suppress
  // "payment due" / pending obligations and show a cycle-complete state instead.
  const cycleComplete = susuOn && isCycleComplete(rounds, memberData?.group_id);
  // Was THIS member part of the (effective) cycle? A member added between cycles
  // has no collector-round in it — so the "everyone's collected / all square"
  // banner would be a false claim for them; show a "joins next cycle" note instead.
  const iAmInThisCycle = susuOn && hands.some(
    h => getCollectionPosition(h, rounds, effectiveCycle) != null
  );

  // Actual collection date for a collected hand (confirmed / admin-advanced / sent) —
  // used to replace the future estimate on already-collected hands.
  const getCollectedDate = (hand) => {
    // Payout date (Model B) or the round's confirmed_at (direct, no payout).
    const ts = collectedTimestamp(hand, effectiveCycle, groupPayouts, rounds);
    return ts ? new Date(ts).toLocaleDateString() : null;
  };

  // How a hand collected: 'confirmed' (member-confirmed) vs 'advanced_unconfirmed'
  // (admin-advanced, no member confirmation) — preserves the audit distinction.
  const getCollectedVia = (hand) => {
    const p = groupPayouts.find(x => String(x.hand_id) === String(hand.id)
      && ['confirmed', 'advanced_unconfirmed'].includes(x.status));
    return p?.status || null;
  };

  // Round progress (Stage 9): X = current round_number, Y = total rounds (= active hands).
  const roundProgress = (susuOn && currentRound)
    ? `Round ${currentRound.round_number} of ${currentRound.contributor_count}`
    : null;
  // Final round of the cycle (live round is the last). The current collector then
  // has no next round, so the Model B collect copy drops "contribute again next round".
  const isFinalRound = !!currentRound
    && Number(currentRound.round_number) === Number(currentRound.contributor_count);

  const statusClass = (status) => PAYMENT_STATUS_CLASSES[status] || 'bg-warning-100 text-warning-800';
  const statusLabel = (status) => PAYMENT_STATUS_LABELS[status] || (status?.toUpperCase() ?? 'PENDING');

  // "This Cycle" tiles are scoped to the current cycle (effectiveCycle). Before
  // any cycle exists effectiveCycle is null → $0 (correct: nothing this cycle).
  const totalContributed = effectiveCycle == null ? 0 : payments
    .filter(p => (p.status === 'paid' || p.status === 'covered')
              && Number(p.cycle_number) === Number(effectiveCycle))
    .reduce((sum, p) => sum + (p.amount_received || 0), 0);

  // Total collected this cycle. OR pattern (provably Model-B-safe): my confirmed/
  // admin-advanced payout amount when a payout exists (Model B — unchanged), else
  // the pot of my confirmed collection round(s) this cycle ((N-1) × contribution)
  // for direct groups, which have no payout. myPayouts is own payouts (full — has
  // payout_amount); groupPayouts is the amount-free group_payout_status view now,
  // so it can't source this $ figure. Formula (not summed actuals) because peers'
  // payments aren't in the member's scope; exact today (fixed contributions).
  const totalCollected = memberCollectedAmount(hands, effectiveCycle, myPayouts, rounds);

  // ── Amounts owed (late fees are a SEPARATE member-level debt) ──────────────
  // feesOutstanding never touches contribution/pot math; contributionOwed reads
  // the same payments the board already shows. Total owed just sums the two for
  // display — the engine/pot are untouched.
  const feesOutstanding = outstandingTotal(memberFees);
  const contributionOwed = (susuOn && currentRound && !cycleComplete)
    ? hands
        .filter(h => h.is_active !== false)
        .reduce((sum, h) => {
          const p = getHandPayment(h);
          return (p && ['paid', 'covered'].includes(p.status))
            ? sum
            : sum + Number(p?.amount_expected ?? h.contribution_amount ?? 0);
        }, 0)
    : 0;
  const totalOwed = contributionOwed + feesOutstanding;

  // Layer 1 (pairs with the Stage 10 engine invariants): hide a deselected/
  // inactive hand's phantom obligations from the member's OWN views. Render-
  // level only — `hands`/`payments` stay intact for the aggregates above
  // (contributionOwed already filters is_active; this makes the cards agree).
  //   * visibleHands: drop inactive hands so no phantom card / Contribution /
  //     Payment Due / "I Paid" shows. Mirrors the admin dashboard's filter.
  //   * visiblePayments: drop only a PENDING row on an inactive hand (the
  //     phantom obligation). Real history (paid/covered/submitted) on a hand
  //     that is now sitting out is preserved.
  const visibleHands = hands.filter(h => h.is_active !== false);
  const visiblePayments = payments.filter(p => {
    const h = hands.find(x => String(x.id) === String(p.hand_id));
    return !(h && h.is_active === false && p.status === 'pending');
  });

  // Reload only the fee ledger after a member submits/edits (separate from the
  // contribution refetch; never touches payments/rounds).
  const reloadFees = async () => {
    if (memberData?.id) setMemberFees(await fetchMemberFees([memberData.id]));
  };

  const currentStreak = (() => {
    // All-time running streak (not cycle-scoped). A 'covered' payment means the
    // obligation was met, so it counts toward the streak like 'paid'.
    const sorted = [...payments].sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
    let streak = 0;
    for (const p of sorted) {
      if (p.status === 'paid' || p.status === 'covered') streak++;
      else break;
    }
    return streak;
  })();

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-error-600">{error}</div>
    </div>
  );

  if (!memberData) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center p-8">
        <h2 className="text-xl font-bold text-neutral-900 mb-2">Welcome to EsuSol</h2>
        <p className="text-neutral-600">Your admin will add you to a group shortly.</p>
        <button
          onClick={() => { supabaseClient.auth.signOut(); navigate('/'); }}
          className="mt-4 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg"
        >
          Sign Out
        </button>
        {/* Any logged-in member can delete their account, even before joining a group. */}
        <div className="mt-4">
          <button
            onClick={() => setShowDeleteAccount(true)}
            className="text-sm text-error-600 underline hover:text-error-700"
          >
            Delete my account
          </button>
        </div>
      </div>
      {showDeleteAccount && <DeleteAccountModal onClose={() => setShowDeleteAccount(false)} />}
    </div>
  );

  // Home-tab actionable alerts — derived from already-loaded member data
  // (same conditions the Payments/Schedule sections use; no new fetching).
  const hasTurnToCollect = hands.some(isTurnToCollect);
  // Direct groups: warn the CURRENT collector if they haven't set any payment
  // details, so the members paying them this round have somewhere to send it.
  const hasPaymentDetails = !!(memberData?.zelle_identifier || memberData?.cashapp_handle || memberData?.venmo_handle);
  const collectorMissingDetails =
    groupData?.money_flow_model === 'direct_to_collector' && hasTurnToCollect && !hasPaymentDetails;
  const hasPaymentDue = !cycleComplete && hands.some((h) => {
    if (h.is_active === false) return false;   // deselected — not participating (Layer 1 parity)
    if (isTurnToCollect(h)) return false;      // collector doesn't owe this round (Model B)
    const s = getHandStatus(h);
    return s === 'pending' || s === 'late' || s === 'rejected' || s === 'voided';
  });

  return (
    <div className="min-h-screen bg-neutral-50">
      <div
        className="max-w-6xl mx-auto px-4 pt-8"
        style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}
      >

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Member Dashboard</h1>
            <p className="text-neutral-600">Welcome back, {memberData.name}</p>
            {roundProgress && (
              <p className="text-sm font-medium text-primary-400 mt-0.5">{roundProgress}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => navigate('/how-to')}
              className="px-4 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700 hover:bg-neutral-50"
            >
              How It Works
            </button>
            <button
              onClick={() => navigate('/faq')}
              className="px-4 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700 hover:bg-neutral-50"
            >
              Help / FAQ
            </button>
            <button
              onClick={() => { localStorage.clear(); supabaseClient.auth.signOut(); navigate('/'); }}
              className="px-4 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700 hover:bg-neutral-50"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* In-app notifications */}
        {notifications.length > 0 && (
          <div className="mb-6 space-y-2">
            {notifications.map(notif => (
              <div
                key={notif.id}
                className={`flex items-start justify-between rounded-lg p-4 bg-neutral-100 border-l-4 ${
                  notif.type === 'payment_approved' ? 'border-success-500' :
                  notif.type === 'payment_rejected' ? 'border-error-500' :
                  'border-primary-500'
                }`}
              >
                <p className="text-sm font-medium text-neutral-900">
                  {notif.message}
                </p>
                <button
                  onClick={() => handleDismissNotification(notif.id)}
                  className="ml-3 flex-shrink-0 text-neutral-400 hover:text-neutral-600 text-lg leading-none"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pending payout confirmations */}
        {pendingPayouts.length > 0 && (
          <div className="bg-neutral-100 border border-warning-500 rounded-lg p-4 mb-8">
            <p className="font-medium text-warning-400 mb-3">⚡ You have pending payouts to confirm:</p>
            {pendingPayouts.map(payout => (
              <div
                key={payout.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-neutral-50 rounded-lg p-3 mb-2 border border-neutral-200 gap-2"
              >
                <div>
                  <p className="font-medium text-neutral-900">
                    Payout of ${payout.payout_amount?.toFixed(2)} via {payout.payment_method}
                  </p>
                  <p className="text-sm text-neutral-600">Date sent: {payout.date_sent}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleConfirmPayout(payout.id)}
                    className="flex-1 sm:flex-none px-3 py-2 bg-success-600 text-white rounded-lg text-sm hover:bg-success-700"
                  >
                    ✓ Confirm Receipt
                  </button>
                  <button
                    onClick={() => handleDisputePayout(payout.id)}
                    className="flex-1 sm:flex-none px-3 py-2 bg-error-600 text-white rounded-lg text-sm hover:bg-error-700"
                  >
                    ✗ Dispute
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Payments tab: hands + payment status */}
        {activeTab === 'payments' && (
        <div className="space-y-6 mb-6">

          {/* Collector in-round progress — BOTH models, read-only (the emotional peak). */}
          {susuOn && hasTurnToCollect && (
            <CollectorCollectionProgress
              groupId={memberData.group_id}
              collectorHandId={currentRound?.collector_hand_id}
              isDirect={groupData?.money_flow_model === 'direct_to_collector'}
              refreshKey={collectorRefresh}
              onJumpToConfirm={() => {
                document.getElementById('collector-confirm-actions')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          )}

          {/* Direct-to-collector: review + confirm payments made to me this round (C3) */}
          {susuOn && groupData?.money_flow_model === 'direct_to_collector' && hasTurnToCollect && (
            <div id="collector-confirm-actions">
              <CollectorPaymentsToMe
                groupId={memberData.group_id}
                onChanged={() => { fetchData(); setCollectorRefresh(n => n + 1); }}
              />
            </div>
          )}

          {/* My Hands */}
          <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">My Hands</h2>
            {visibleHands.length === 0 ? (
              <p className="text-neutral-500">No hands assigned yet.</p>
            ) : (
              [...visibleHands]
                // Member-LOCAL order: their own hands by collection order.
                .sort((a, b) =>
                  (getCollectionPosition(a, rounds, effectiveCycle) ?? a.position_number)
                  - (getCollectionPosition(b, rounds, effectiveCycle) ?? b.position_number))
                .map((hand, idx) => {
                const status = getHandStatus(hand);
                const payment = getHandPayment(hand);
                const paymentDueDate = getPaymentDueDate();
                const daysUntilDue = getDaysUntilPaymentDue();
                const payoutDate = getPayoutCollectionDate(hand);
                const isMyTurn = isTurnToCollect(hand);
                const collectedThisCycle = susuOn && handCollectedThisCycle(hand);
                const owesThisRound = susuOn && memberRoundState(hand) === MEMBER_STATE.CONTRIBUTE_NOW;

                return (
                  <div
                    key={hand.id}
                    className={`border rounded-lg p-4 mb-3 bg-neutral-100 ${
                      isMyTurn ? 'border-primary-500 border-l-4' : 'border-neutral-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        {/* Member-LOCAL hand number (1..N of the member's own hands), not the group-wide position. */}
                        <p className="font-medium text-neutral-900">Hand {idx + 1}</p>
                        <p className="text-sm text-neutral-600">{groupData?.group_name}</p>
                        {/* Model B: the round's collector owes no contribution, so hide
                            Contribution / Payment Due for them (isMyTurn = UP_NOW). */}
                        {!isMyTurn && (
                          <p className="text-sm text-neutral-600">
                            Contribution: ${hand.contribution_amount?.toFixed(2)}
                          </p>
                        )}
                        {/* Gate purely on collectedThisCycle so a mid-cycle collection also relabels. */}
                        {!isMyTurn && !collectedThisCycle && (
                          <p className="text-sm text-neutral-600">
                            Payment Due: {paymentDueDate || 'Not set'}
                          </p>
                        )}
                        {collectedThisCycle ? (
                          <p className="text-sm text-success-700">
                            {getCollectedVia(hand) === 'advanced_unconfirmed' ? 'Collected (admin-advanced)' : 'Collected'}: {getCollectedDate(hand) || '—'}
                          </p>
                        ) : (
                          <p className="text-sm text-neutral-600">
                            Collects: {payoutDate || 'Not set'}
                          </p>
                        )}
                        {isMyTurn && (
                          <p className="text-sm text-primary-600 mt-1">
                            <span className="font-medium">🎯 Your turn to collect!</span>{' '}
                            You skip your contribution this round and collect the pot.
                            {!isFinalRound && " You'll contribute again next round."}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        {/* Badge precedence (Model B): not in this cycle's rotation →
                            joins next; cycle complete → Collected; my live collect turn →
                            Collecting; otherwise the CURRENT round's status (so after
                            collecting, a submission for the next round shows "Submitted",
                            not a sticky "Collected"). */}
                        {susuOn && getCollectionPosition(hand, rounds, effectiveCycle) == null ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-200 text-neutral-700">
                            Joins next cycle
                          </span>
                        ) : cycleComplete ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-800">
                            ✓ Collected
                          </span>
                        ) : isMyTurn ? (
                          /* Model B: the collector owes nothing this round — show a
                             collect pill, not the default-'pending' status. */
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                            Collecting
                          </span>
                        ) : (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass(status)}`}>
                            {statusLabel(status)}
                          </span>
                        )}
                        {/* Only nudge when the hand actually owes a contribution
                            this round. owesThisRound (CONTRIBUTE_NOW) is false once
                            the hand has paid/covered/submitted or collected, so the
                            countdown no longer shows on a settled or collected hand. */}
                        {owesThisRound && daysUntilDue !== null && daysUntilDue > 0 && (
                          <p className="text-xs text-neutral-500 mt-1">{daysUntilDue}d until due</p>
                        )}
                      </div>
                    </div>

                    {/* Status indicators */}
                    {status === 'member_submitted' && (
                      <div className="mt-2 bg-warning-50 border border-warning-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-warning-800">⏳ Pending Admin Approval</p>
                      </div>
                    )}
                    {status === 'paid' && (
                      <div className="mt-2 bg-success-50 border border-success-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-success-800">✓ Payment Confirmed</p>
                      </div>
                    )}
                    {status === 'rejected' && (
                      <div className="mt-2 bg-error-50 border border-error-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-error-800">
                          ⚠️ Rejected{payment?.rejection_reason ? ` — ${payment.rejection_reason}` : ''}
                        </p>
                      </div>
                    )}
                    {status === 'late' && (
                      <div className="mt-2 bg-error-50 border border-error-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-error-800">⚠️ Payment Overdue</p>
                      </div>
                    )}
                    {/* Voided = we couldn't confirm the payment on the admin side; the
                        member likely already paid. Frame as RE-SUBMIT / re-verify, never
                        "pay again". Non-alarming (warning, not error). COPY: draft for
                        Frannie to finalize. */}
                    {status === 'voided' && (
                      <div className="mt-2 bg-warning-50 border border-warning-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-warning-800">
                          This payment needs to be confirmed again. Please resubmit your payment details and receipt.
                        </p>
                      </div>
                    )}

                    {/* Already collected this cycle but still owes this round's
                        contribution — acknowledge it so contributing again reads
                        as expected, not a demotion. */}
                    {collectedThisCycle && owesThisRound && (
                      <div className="mt-2 bg-success-50 border border-success-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-success-800">
                          ✓ You've collected this cycle — now contribute for this round so the next member gets theirs.
                        </p>
                      </div>
                    )}

                    {/* Action buttons (suppressed once the cycle is complete) */}
                    {/* Gate on an active cycle (live round exists), mirroring the
                        admin-side susuOn && currentRound signal. Pre-cycle there is
                        no live round, so status defaults to 'pending' — without this
                        guard the button would open a submit modal that can never
                        succeed (the client insert guard and the DB open-round trigger
                        both reject a no-live-round write). Hidden pre-cycle.
                        Model B: the live collector does NOT contribute in the round
                        they collect, so hide "I Paid" for them (!isMyTurn). isMyTurn
                        is UP_NOW — the live collector only. The next collector
                        (UP_NEXT) still owes this round and keeps the button, which is
                        why this excludes UP_NOW specifically, not CONTRIBUTE_NOW. */}
                    {susuOn && currentRound && !cycleComplete && !isMyTurn && (status === 'pending' || status === 'late' || status === 'rejected' || status === 'voided') && (
                      <button
                        onClick={() => { setSubmittingHand(hand); setShowSubmitModal(true); }}
                        className="mt-3 w-full px-3 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-medium hover:bg-primary-700"
                      >
                        I Paid
                      </button>
                    )}
                    {susuOn && currentRound && !cycleComplete && status === 'member_submitted' && (
                      <button
                        onClick={() => { setSubmittingHand(hand); setShowSubmitModal(true); }}
                        className="mt-3 w-full px-3 py-2 bg-warning-600 text-white rounded-lg text-sm font-medium hover:bg-warning-700"
                      >
                        Edit Submission
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* My Payment Status */}
          <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">My Payment Status</h2>
            {visiblePayments.length === 0 ? (
              <p className="text-neutral-500">No payment history yet.</p>
            ) : (
              visiblePayments.map(payment => (
                <div
                  key={payment.id}
                  className="border border-neutral-100 rounded-lg p-3 mb-2"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-neutral-900">
                        ${(payment.amount_received ?? payment.submitted_amount ?? 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-neutral-600">
                        {payment.payment_date
                          || (payment.submitted_at
                            ? new Date(payment.submitted_at).toLocaleDateString()
                            : '—')}
                      </p>
                      <p className="text-sm text-neutral-600">
                        {payment.payment_method || payment.submitted_method || '—'}
                      </p>
                      {/* Direct: who this payment went to (the round's collector). */}
                      {groupData?.money_flow_model === 'direct_to_collector' && payment.round_id && (() => {
                        const r = rounds.find(rr => String(rr.id) === String(payment.round_id));
                        const coll = r && groupMembers.find(m => String(m.id) === String(r.collector_member_id));
                        return coll ? <p className="text-sm text-neutral-600">Paid to: {coll.name}</p> : null;
                      })()}
                      {(payment.reference_number || payment.submitted_reference) && (
                        <p className="text-xs text-neutral-500 break-all">Ref: {payment.reference_number || payment.submitted_reference}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass(payment.status)}`}>
                      {statusLabel(payment.status)}
                    </span>
                  </div>
                  {/* All receipts for this contribution (primary + split-payment images). */}
                  <ProofThumbnails paymentId={payment.id} primaryPath={payment.proof_path} className="mt-2" />
                </div>
              ))
            )}
          </div>
        </div>
        )}

        {/* Home tab: actionable alerts + overview summary */}
        {activeTab === 'home' && (
          <>
          {cycleComplete && (iAmInThisCycle ? (
            <div className="mb-6 rounded-lg p-5 border border-success-500 bg-success-50 text-center">
              <p className="text-2xl mb-1" aria-hidden="true">🎉</p>
              <p className="text-lg font-bold text-success-800">Cycle Complete</p>
              <p className="text-sm text-success-700 mt-1">Everyone has collected this cycle — you're all square.</p>
            </div>
          ) : (
            <div className="mb-6 rounded-lg p-5 border border-primary-300 bg-primary-50 text-center">
              <p className="text-lg font-bold text-primary-800">You'll join the next cycle</p>
              <p className="text-sm text-primary-700 mt-1">This cycle finished before you joined — you'll be included when the next cycle starts.</p>
            </div>
          ))}
          {(hasTurnToCollect || hasPaymentDue) && (
            <div className="space-y-3 mb-6">
              {/* Direct: the collector hasn't set payment details — members can't pay
                  them. Most urgent, so it sits above the generic turn banner. COPY:
                  Frannie draft. Clears once they save a handle (fetchData on save). */}
              {collectorMissingDetails && (
                <button
                  type="button"
                  onClick={() => changeTab('account')}
                  className="w-full text-left flex items-center justify-between gap-3 bg-warning-50 border border-warning-500 border-l-4 rounded-lg p-4 hover:bg-warning-100"
                >
                  <div>
                    <p className="font-semibold text-warning-900">💸 Add your payment details</p>
                    <p className="text-sm text-warning-800">It's your turn to collect! Add your Zelle, Cash App, or Venmo so members know how to pay you.</p>
                  </div>
                  <svg className="w-5 h-5 text-warning-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              )}
              {hasTurnToCollect && (
                <button
                  type="button"
                  onClick={() => changeTab('payments')}
                  className="w-full text-left flex items-center justify-between gap-3 bg-neutral-100 border border-primary-500 border-l-4 rounded-lg p-4 hover:bg-neutral-50"
                >
                  <div>
                    <p className="font-semibold text-primary-400">🎯 It's your turn to collect!</p>
                    <p className="text-sm text-neutral-600">Tap to view your hand and payout details.</p>
                  </div>
                  <svg className="w-5 h-5 text-primary-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              )}
              {hasPaymentDue && (
                <>
                  <button
                    type="button"
                    onClick={() => changeTab('payments')}
                    className="w-full text-left flex items-center justify-between gap-3 bg-neutral-100 border border-warning-500 border-l-4 rounded-lg p-4 hover:bg-neutral-50"
                  >
                    <div>
                      <p className="font-semibold text-warning-400">💸 You have a payment due</p>
                      <p className="text-sm text-neutral-600">Tap to mark it paid.</p>
                    </div>
                    <svg className="w-5 h-5 text-warning-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                  {/* Direct groups: who/how to pay this round's collector, at a glance. */}
                  {groupData?.money_flow_model === 'direct_to_collector' && collectorPayinfo && (
                    <CollectorPayBlock payinfo={collectorPayinfo} className="mt-2" />
                  )}
                </>
              )}
            </div>
          )}
          {/* Amounts Owed — shown only when the group uses late fees. Contribution
              and Late fee are DISTINCT lines; Total owed is their sum (display only). */}
          {groupData?.fees_enabled && (
            <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4">Amounts Owed</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600">Contribution due</span>
                  <span className="font-medium">${contributionOwed.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">Late fees outstanding</span>
                  <span className={`font-medium ${feesOutstanding > 0 ? 'text-error-600' : ''}`}>${feesOutstanding.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t border-neutral-200 pt-3">
                  <span className="font-semibold text-neutral-900">Total owed</span>
                  <span className="font-bold text-neutral-900">${totalOwed.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Late Fees — member handshake. Shown only when the member has fees.
              Fully separate from the contribution flow above. */}
          {groupData?.fees_enabled && memberFees.length > 0 && (
            <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4">Late Fees</h2>
              <div className="space-y-3">
                {memberFees.map(f => (
                  <div key={f.id} className="border border-neutral-200 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-neutral-900">${Number(f.amount || 0).toFixed(2)}</p>
                        {f.note && <p className="text-xs text-neutral-500">{f.note}</p>}
                        <p className="text-xs text-neutral-400">Applied {new Date(f.applied_at).toLocaleDateString()}</p>
                      </div>
                      {f.state === 'resolved' && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-800">
                          ✓ Fee {f.resolution?.event === 'waived' ? 'Waived' : 'Paid'}
                        </span>
                      )}
                      {f.state === 'pending' && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-800">Submitted</span>
                      )}
                    </div>

                    {f.state === 'pending' && (
                      <>
                        <div className="mt-2 bg-warning-50 border border-warning-200 rounded-lg p-2">
                          <p className="text-sm font-medium text-warning-800">⏳ Fee Payment Pending Approval</p>
                        </div>
                        <button onClick={() => setFeeModal(f)}
                          className="mt-2 w-full px-3 py-2 bg-warning-600 text-white rounded-lg text-sm font-medium hover:bg-warning-700">
                          Edit Fee Submission
                        </button>
                      </>
                    )}

                    {f.state === 'outstanding' && (
                      <>
                        {f.rejected && (
                          <div className="mt-2 bg-error-50 border border-error-200 rounded-lg p-2">
                            <p className="text-sm font-medium text-error-800">
                              ⚠️ Fee Submission Rejected{f.rejected.note ? ` — ${f.rejected.note}` : ''}
                            </p>
                          </div>
                        )}
                        <button onClick={() => setFeeModal(f)}
                          className="mt-2 w-full px-3 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-medium hover:bg-primary-700">
                          I Paid Late Fee
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">My Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-neutral-600">Total Contributed This Cycle</span>
                <span className="font-medium">${totalContributed.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Total Collected</span>
                <span className="font-medium">${totalCollected.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Current Streak</span>
                <span className="font-medium">{currentStreak} payments</span>
              </div>
            </div>
            {myConsent && (
              <div className="mt-4 pt-3 border-t border-neutral-200">
                <p className="text-sm text-neutral-500">
                  You agreed to the group agreement on {new Date(myConsent.agreed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                  {' '}Agreement {myConsent.terms_version}
                  {myConsent.privacy_accepted && myConsent.privacy_version ? ` · Privacy ${myConsent.privacy_version}` : ''}
                  {myConsent.tc_accepted && myConsent.tc_version ? ` · Terms ${myConsent.tc_version}` : ''}.
                </p>
                <button
                  type="button"
                  onClick={() => downloadAgreementPdf({
                    fullName: memberData.name,
                    signature: myConsent.signature,
                    agreedAt: myConsent.agreed_at,
                    version: myConsent.terms_version,
                    sha256: myConsent.terms_sha256,
                    privacyVersion: myConsent.privacy_version,
                    tcVersion: myConsent.tc_version,
                  }).catch(() => {})}
                  className="mt-1 text-sm text-primary-600 underline hover:text-primary-700"
                >
                  Download agreement
                </button>
              </div>
            )}

            {/* Certified record (H6). Own-record-only; certified-only export (requires
                an organizer attestation). The on-screen record is visible regardless. */}
            <div className="mt-4 pt-3 border-t border-neutral-200">
              <p className="text-sm font-medium text-neutral-900">My savings-club record</p>
              {myRecord && myRecord.organizer && myRecord.organizer.complete === false ? (
                <p className="text-xs text-warning-700 mt-1">
                  Your organizer needs to complete their full name before this record can be certified.
                </p>
              ) : myRecord && !myRecord.attestation ? (
                <p className="text-xs text-neutral-500 mt-1">
                  Not yet certified — ask your organizer to certify your record before you export it.
                </p>
              ) : myRecord && myRecord.attestation.current === false ? (
                <p className="text-xs text-warning-700 mt-1">
                  Your record changed since it was certified — ask your organizer to re-attest before exporting.
                </p>
              ) : (
                <p className="text-xs text-neutral-500 mt-1">
                  A self-contained PDF of your contributions and collections for a mortgage file.
                </p>
              )}
              {recordExportError && <p className="text-xs text-error-600 mt-1">{recordExportError}</p>}
              <button
                type="button"
                onClick={handleExportMyRecord}
                disabled={exportingRecord || !myRecord?.attestation || myRecord?.attestation?.current === false || (myRecord?.organizer && myRecord.organizer.complete === false)}
                className="mt-1 text-sm text-primary-600 underline hover:text-primary-700 disabled:no-underline disabled:text-neutral-400"
              >
                {exportingRecord ? 'Preparing…' : 'Download my record (PDF)'}
              </button>
            </div>
          </div>
          </>
        )}

        {/* Schedule tab: rotation */}
        {activeTab === 'schedule' && (
          <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Group Rotation View</h2>
            {(() => {
              if (!groupData?.start_date || allGroupHands.length === 0) {
                return <p className="text-neutral-500">No rotation data yet.</p>;
              }

              // The live round's collector is "next to collect" (authoritative
              // order from the rounds table — not collected_amount or dates).
              const nextToCollect = allGroupHands.find(
                h => String(h.id) === String(currentRound?.collector_hand_id)
              );

              // My next collection = my uncollected hand with the EARLIEST COLLECTION
              // ORDER (round_number), not the lowest assignment position.
              const collPos = (h) => getCollectionPosition(h, rounds, effectiveCycle) ?? h.position_number;
              const myGroupHands = allGroupHands.filter(
                h => String(h.member_db_id) === String(memberData.id)
              );
              const myUncollected = myGroupHands.filter(h => !handCollectedThisCycle(h));
              const myNextHand = myUncollected.length
                ? myUncollected.reduce((best, h) => (collPos(h) < collPos(best) ? h : best))
                : null;
              const myAllCollected = myGroupHands.length > 0 && myUncollected.length === 0;

              // My next collection date — READ from the stored round schedule (Stage 9).
              const myNextDate = myNextHand
                ? formatScheduleDate(getScheduledCollectionDate(myNextHand, rounds, effectiveCycle))
                : null;

              return (
                <>
                  {cycleComplete ? (
                    <div className="mb-4 bg-success-50 border border-success-500 rounded-lg p-3">
                      <p className="text-sm font-semibold text-success-800">
                        ✓ Cycle complete — you've collected this cycle
                      </p>
                    </div>
                  ) : myNextHand ? (
                    <div className="mb-4 bg-neutral-100 border border-primary-500 rounded-lg p-3">
                      {isTurnToCollect(myNextHand) ? (
                        <p className="text-sm font-medium text-primary-400">🎯 Your turn to collect now!</p>
                      ) : (
                        <p className="text-sm font-medium text-primary-400">
                          Your next turn: Position {collPos(myNextHand)}
                          {myNextDate ? ` — ${myNextDate}` : ''}
                        </p>
                      )}
                    </div>
                  ) : myAllCollected && (
                    <div className="mb-4 bg-success-50 border border-success-500 rounded-lg p-3">
                      <p className="text-sm font-semibold text-success-800">
                        ✓ You've collected all your turns this cycle
                      </p>
                    </div>
                  )}

                  <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                    {[...allGroupHands]
                      // Active roster only (Model B parity) — exclude deselected hands.
                      .filter(hand => hand.is_active !== false)
                      // Order the group rotation by COLLECTION order (round_number).
                      .sort((a, b) => collPos(a) - collPos(b))
                      .map(hand => {
                      const handMember = groupMembers.find(m => String(m.id) === String(hand.member_db_id));
                      const isMyHand = String(hand.member_db_id) === String(memberData.id);
                      const hasCollected = handCollectedThisCycle(hand);
                      const isNext = String(nextToCollect?.id) === String(hand.id);
                      // Status mirrors the admin rotation view: COLLECTED / NEXT / WAITING.
                      const status = hasCollected ? 'COLLECTED' : isNext ? 'NEXT' : 'WAITING';
                      // Date line: actual collected date once collected, else the
                      // scheduled collection date (same derivation as the admin view).
                      const collectedDateStr = getCollectedDate(hand);
                      const scheduledDateStr = formatScheduleDate(getScheduledCollectionDate(hand, rounds, effectiveCycle));

                      return (
                        <div
                          key={hand.id}
                          className={`rounded-lg px-3 py-2.5 ${
                            isMyHand
                              ? 'bg-neutral-100 border border-primary-500'
                              : hasCollected
                              ? 'bg-neutral-50 border border-neutral-100 opacity-60'
                              : 'bg-neutral-50 border border-neutral-100'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-semibold text-neutral-500 shrink-0 w-6 text-right">
                                {collPos(hand)}.
                              </span>
                              <span className={`text-sm font-medium truncate ${isMyHand ? 'text-primary-400' : 'text-neutral-900'}`}>
                                {handMember?.name || 'Unassigned'}
                              </span>
                              {isMyHand && (
                                <span className="text-xs text-primary-500 font-medium shrink-0">← You</span>
                              )}
                            </div>
                            <p className="text-xs text-neutral-400 mt-0.5 ml-7">
                              {hasCollected
                                ? `Collected: ${collectedDateStr || '—'}`
                                : `Collection Date: ${scheduledDateStr || 'Not set'}`}
                            </p>
                          </div>
                          <div className="ml-2 shrink-0">
                            {status === 'COLLECTED' ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">
                                ✓ Collected
                              </span>
                            ) : status === 'NEXT' ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-800">
                                Next
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-200 text-neutral-500">
                                Waiting
                              </span>
                            )}
                          </div>
                          </div>
                          {/* Direct: who paid into this collector's completed turn (Stage 33). */}
                          {groupData?.money_flow_model === 'direct_to_collector' && hasCollected && (() => {
                            const contribs = groupContributions.filter(
                              c => Number(c.round_number) === collPos(hand)
                                && Number(c.cycle_number) === Number(effectiveCycle)
                                && c.contributor_name
                            );
                            if (!contribs.length) return null;
                            return (
                              <p className="text-xs text-neutral-500 mt-1 ml-7">
                                <span className="font-medium text-neutral-600">Paid by: </span>
                                {contribs.map((c, i) => (
                                  <span key={c.payment_id}>
                                    {i > 0 ? ', ' : ''}{c.contributor_name} (${Number(c.amount || 0).toFixed(2)}{c.paid_date ? `, ${new Date(c.paid_date).toLocaleDateString()}` : ''}{c.status ? `, ${c.status}` : ''})
                                  </span>
                                ))}
                              </p>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Account tab: preferences + support */}
        {activeTab === 'account' && (
          <>
        <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Notification Preferences</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">WhatsApp Number</label>
              <input
                type="text"
                value={prefs.whatsapp_number}
                onChange={e => setPrefs({ ...prefs, whatsapp_number: e.target.value })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">SMS Number</label>
              <input
                type="text"
                value={prefs.sms_number}
                onChange={e => setPrefs({ ...prefs, sms_number: e.target.value })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
              <input
                type="email"
                value={prefs.email}
                onChange={e => setPrefs({ ...prefs, email: e.target.value })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Notification Preference</label>
              <select
                value={prefs.notification_preference}
                onChange={e => setPrefs({ ...prefs, notification_preference: e.target.value })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              >
                <option value="whatsapp_only">WhatsApp only</option>
                <option value="whatsapp_sms">WhatsApp + SMS</option>
                <option value="sms_only">SMS only</option>
                <option value="email">Email</option>
                <option value="in_app">In-app only</option>
              </select>
            </div>
          </div>
          {/* Payment handles for direct-to-collector groups. Optional; self-editable.
              Surfaced to members paying you ONLY during your collecting turn via
              get_round_collector_payinfo (Stage 29) — never a group-wide directory. */}
          <h3 className="text-sm font-semibold text-neutral-900 mt-6 mb-1">Payment Details</h3>
          <p className="text-xs text-neutral-500 mb-3">
            Optional. For direct-payment groups — shown to members paying you only while it's your collecting turn.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Zelle (email or phone)</label>
              <input
                type="text"
                value={prefs.zelle_identifier}
                onChange={e => setPrefs({ ...prefs, zelle_identifier: e.target.value })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Cash App ($cashtag)</label>
              <input
                type="text"
                value={prefs.cashapp_handle}
                onChange={e => setPrefs({ ...prefs, cashapp_handle: e.target.value })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Venmo (@handle)</label>
              <input
                type="text"
                value={prefs.venmo_handle}
                onChange={e => setPrefs({ ...prefs, venmo_handle: e.target.value })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>
          <button
            onClick={handleUpdatePrefs}
            className="mt-4 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm hover:bg-primary-700"
          >
            Update Preferences
          </button>
        </div>

        {/* Display name (Stage 46) — below Notification Preferences (optional setting).
            Copy finalized (Frannie). Its Save button shares handleUpdatePrefs. */}
        <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-1">Display name</h2>
          <p className="text-xs text-neutral-500 mb-3">
            Choose how your name appears to others in your circle. Your admin and your official records
            always show your real name. Leave blank to just use your real name.
          </p>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Display name (optional)</label>
          <input
            type="text"
            maxLength={40}
            value={prefs.display_alias}
            onChange={e => setPrefs({ ...prefs, display_alias: e.target.value })}
            placeholder={memberData?.name || 'Your name'}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2"
          />
          <p className="mt-1 text-xs text-neutral-400">
            {prefs.display_alias?.trim()
              ? `Other members will see "${prefs.display_alias.trim().slice(0, 40)}".`
              : `Other members will see your real name, ${memberData?.name || ''}.`}
            {' '}Up to 40 characters.
          </p>
          <button
            onClick={handleUpdatePrefs}
            className="mt-4 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm hover:bg-primary-700"
          >
            Save display name
          </button>
        </div>

        {/* Delete account (Stage 40). FRANNIE DRAFT copy. */}
        <div className="bg-neutral-100 rounded-lg shadow-sm border border-error-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-error-700 mb-1">Delete my account</h2>
          <p className="text-sm text-neutral-500 mb-3">
            Permanently delete your account and personal details. Your past contributions stay on record
            for your groups as "Former member". This can't be undone.
          </p>
          <button
            onClick={() => setShowDeleteAccount(true)}
            className="px-4 py-2 border border-error-300 text-error-700 rounded-lg text-sm font-medium hover:bg-error-50"
          >
            Delete my account
          </button>
        </div>

        <div className="mt-6 text-center">
          <ReportProblemButton />
        </div>
          </>
        )}

      </div>

      <BottomNav active={activeTab} onChange={changeTab} />

      {showDeleteAccount && <DeleteAccountModal onClose={() => setShowDeleteAccount(false)} />}

      {/* Messaging re-consent (piece 3) — one-time for in-a-group members without the
          current messaging consent version. Dismissible; re-prompts next login until accepted. */}
      {needsReconsent && !reconsentDismissed && myConsent && (
        <ReconsentModal
          memberData={memberData}
          myConsent={myConsent}
          onAccepted={() => setNeedsReconsent(false)}
          onDismiss={() => setReconsentDismissed(true)}
        />
      )}

      {/* Payment submit / edit modal */}
      {showSubmitModal && submittingHand && (
        <MemberPaymentSubmitModal
          hand={submittingHand}
          existingPayment={getHandPayment(submittingHand)}
          memberData={memberData}
          collectorPayinfo={collectorPayinfo}
          onClose={() => { setShowSubmitModal(false); setSubmittingHand(null); }}
          onSuccess={() => { setShowSubmitModal(false); setSubmittingHand(null); fetchData(); }}
        />
      )}

      {feeModal && (
        <FeePaymentSubmitModal
          fee={feeModal}
          onClose={() => setFeeModal(null)}
          onSuccess={async () => { setFeeModal(null); await reloadFees(); }}
        />
      )}
    </div>
  );
};

export default MemberDashboard;
